import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationStart, Router } from '@angular/router';
import fontawesome from '@fortawesome/fontawesome';
import { faFile, faFilePdf, faFilePowerpoint } from '@fortawesome/fontawesome-free-regular';
import { faCheck, faTimes } from '@fortawesome/fontawesome-free-solid';
import { AllCountriesService, WindowUtilitiesService } from '@grid-ui/common';
import { environment } from '@grid-ui/environment';
import {
  WhoAmIUserPersonalInformationChange,
  getWhoAmI,
  selectWhoAmI,
  selectWhoAmIPersonalInformation,
  selectWhoAmIUserSavingStatus,
  whoAmIResetSaveUser,
  whoAmISaveUser,
} from '@grid-ui/whoami';
import { NgbConfig } from '@ng-bootstrap/ng-bootstrap';
import { Store, select } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { GridAnalyticsEventService } from '../api-services';
import { AccountSettingsModalService, AppState, TopUserMenuActionTypes, TopUserMenuActionsUnion } from '../core';
import { AccountSettingsModalResolvedValues } from '../core/app-top-area/account-settings-modal/models/account-settings-modal-resolved-values.enum';
import { PrivacySettingsActionTypes, PrivacySettingsActionsUnion, PrivacySettingsStorageService } from '../privacy-settings';

@Component({
  selector: 'mc-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
  host: {
    class: 'Grid',
  },
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class GridComponent implements OnInit, AfterViewInit, OnDestroy {
  public isSplashScreen = false;
  public loading: boolean;
  public isPreviewMode: boolean;

  private readonly subscriptions = new Subscription();

  private readonly defaultChangePasswordHref = '/accounts/password/change/';

  constructor(
    ngbConfig: NgbConfig,
    public readonly privacySettingsStorageService: PrivacySettingsStorageService,
    private readonly router: Router,
    private readonly store: Store<AppState>,
    private readonly accountSettingsModalService: AccountSettingsModalService,
    private readonly allCountriesService: AllCountriesService,
    private readonly windowUtilitiesService: WindowUtilitiesService,
    private readonly gridAnalyticsEventService: GridAnalyticsEventService,
  ) {
    fontawesome.library.add(faCheck, faTimes, faFile, faFilePdf, faFilePowerpoint);
    if (!environment.production) {
      ngbConfig.animation = false;
    }
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.router.url === '/' || this.router.url === '/home'),
      )
      .subscribe((v) => (this.isSplashScreen = v));
  }

  public ngOnInit(): void {
    this.store.dispatch(getWhoAmI());

    this.store
      .select(selectWhoAmI)
      .pipe(take(1))
      .subscribe((whoAmI) => {
        if (whoAmI) {
          this.isPreviewMode = whoAmI.is_preview_mode;

          this.subscriptions.add(
            this.gridAnalyticsEventService
              .sendSiteLoadedEvent(whoAmI)
              .pipe(take(1))
              .subscribe({ error: () => {} }),
          );
        }
      });
  }

  public ngAfterViewInit(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const url = event.url;

        // HACK: Do not show "app-level" loading component, when navigating
        // within Country Risk/Profile Details or Commodity Risk/Profile Details etc..
        // in favour of their more specific UI behaviour ("persistent" Table of Content)
        const excludedPaths: any[] = [
          // Country Profile Detail
          /^\/country-risk\/profiles\/[0-9]{1,}/,
          // Commodity Profile Detail
          /^\/commodity-risk\/profiles\/[0-9]{1,}/,
          /^\/country-risk\/deep-dive/,
        ];

        this.loading = excludedPaths.some((excludedPath) => excludedPath.test(url) && excludedPath.test(this.router.url)) ? false : true;
      } else if (event instanceof NavigationEnd || event instanceof NavigationCancel) {
        this.loading = false;
      }
    });
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public handleCookiesNotificationBannerAction(action: PrivacySettingsActionsUnion): void {
    switch (action.type) {
      case PrivacySettingsActionTypes.ACCEPT:
        this.privacySettingsStorageService.acceptCookies();
        break;
      case PrivacySettingsActionTypes.REJECT:
        this.privacySettingsStorageService.rejectCookies();
        break;
    }
  }

  public handleTopMenuAction(action: TopUserMenuActionsUnion): void {
    switch (action.type) {
      case TopUserMenuActionTypes.ACCOUNT_SETTINGS:
        this.changeAccountSettings();
        break;
      case TopUserMenuActionTypes.LOGOUT:
        this.windowUtilitiesService.setHref('/accounts/logout');
        break;
      case TopUserMenuActionTypes.SUBSCRIPTIONS:
        void this.router.navigate(['subscriptions/details/country-risk']);
        break;
      case TopUserMenuActionTypes.API_TOKEN:
        void this.router.navigate(['api-token']);
        break;
    }
  }

  private getSaveDispatcher(): (payload: WhoAmIUserPersonalInformationChange) => void {
    const store = this.store;
    return function (payload: WhoAmIUserPersonalInformationChange): void {
      store.dispatch(whoAmISaveUser({ payload }));
    };
  }

  private changeAccountSettings(): void {
    this.store
      .select(selectWhoAmIPersonalInformation)
      .pipe(take(1))
      .subscribe((userPersonalInformation) => {
        if (userPersonalInformation !== null) {
          this.store.dispatch(whoAmIResetSaveUser());
          this.accountSettingsModalService
            .showModal(
              userPersonalInformation,
              this.store.pipe(select(selectWhoAmIUserSavingStatus)),
              this.allCountriesService.getAllCountries(),
              this.getSaveDispatcher(),
            )
            .then((result) => {
              this.store.dispatch(whoAmIResetSaveUser());
              if (result === AccountSettingsModalResolvedValues.CHANGE_PASSWORD) {
                const changePasswordUrl = environment.changePasswordUrl || this.defaultChangePasswordHref;
                this.windowUtilitiesService.setHref(changePasswordUrl);
              }
            })
            .catch(() => {
              /** Do Nothing */
            });
        }
      });
  }
}
